import React, { useState, useRef } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { trackClick } from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { fsEvent, initializeFullstory } from 'js/fullstory';
import moment, { Moment } from 'moment';
import ArrowDownIcon from 'js/design_system/icons/arrow_downward.svg';

type WorkId = string | number;

type Props = {
  workId: WorkId;
  source?: string;
};

const PodcastPlayer = ({ workId, source }: Props) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [times, setTimes] = useState<Moment[]>([]);

  const handlePlay = async () => {
    initializeFullstory();
    trackClick({
      source: 'paper_to_podcast.play',
      entityId: workId,
      extra: {
        work_id: workId,
      },
    });
    fsEvent('paper_to_podcast_clicked');
    setTimes([...times, moment()]);
    audioRef.current?.play();
    setShowFeedback(true);
  };

  const handlePause = async () => {
    let calculatedTime = 0;
    let timeIndex = 0;
    const newTimes = [...times, moment()];

    // Here, `times` is an array of timestamps that alternates between
    // the time the player started and when it was paused.
    // Calculate the sum of differences between each pair to find the total
    // play time. This is mainly to supplement situations when the browser does not report the
    // player's currentTime correctly.
    if (times.length > 0) {
      while (
        typeof newTimes[timeIndex] !== 'undefined' &&
        typeof newTimes[timeIndex + 1] !== 'undefined'
      ) {
        const diff = newTimes[timeIndex + 1].diff(newTimes[timeIndex]);
        calculatedTime += diff;
        timeIndex += 2;
      }
      setTimes(newTimes);
    }

    trackClick({
      source: 'paper_to_podcast.pause',
      entityId: workId,
      extra: {
        work_id: workId,
        current_time: audioRef.current?.currentTime || null,
        total_time: audioRef.current?.duration || null,
        total_play_time: calculatedTime,
      },
    });
  };

  const onSubmitFeedback = () => {
    fetchWithCsrf(
      routes.api_v0_arbitrary_event_index_path({ subdomain_param: 'api' }),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: 'PaperToPodcastFeedback',
          data: {
            feedback: feedbackInput,
            work_id: workId,
            type: 'sap',
          },
          // @ts-ignore
          hit_id: (window.Aedu && Aedu.hit_data && Aedu.hit_data.id) || null,
        }),
      },
    ).then(() => {
      setFeedbackSubmitted(true);
    });
  };

  return (
    <ImpressionWatcher
      style={{ width: '100%' }}
      source={'paper_to_podcast.sap.player'}
      extra={{ workId: workId }}
    >
      <AudioBlock>
        <AudioArea>
          <AudioPlayer
            ref={audioRef}
            controls
            controlsList="nodownload noplaybackrate"
            preload="none"
            src={source}
            onPlay={handlePlay}
            onPause={handlePause}
            style={{
              pointerEvents: 'auto',
              opacity: 1,
            }}
          >
            Your browser does not support the audio element.
          </AudioPlayer>
          <AudioSubTitle>Listen to an AI summary of this paper</AudioSubTitle>
        </AudioArea>
        {showFeedback && (
          <FeedbackForm className={`content ${showFeedback ? 'open' : ''}`}>
            {!feedbackSubmitted ? (
              <>
                <Title>How is your experience listening to this podcast?</Title>
                <InputArea>
                  <Input
                    rows={3}
                    value={feedbackInput}
                    onChange={(e) => setFeedbackInput(e.target.value)}
                    placeholder="Enter feedback here about whether the podcast is interesting, confusing, etc."
                  ></Input>
                  <Button onClick={onSubmitFeedback} secondary={true}>
                    <ArrowContainer>
                      <ArrowDownIcon size={16} />
                    </ArrowContainer>
                    Share Feedback
                  </Button>
                </InputArea>
              </>
            ) : (
              <Subtitle>
                Thank you for sharing your feedback. Together, we're making
                research more accessible for the global academic community.
              </Subtitle>
            )}
          </FeedbackForm>
        )}
      </AudioBlock>
    </ImpressionWatcher>
  );
};

const AudioBlock = styled.div`
  padding-top: 24px;
  padding-bottom: 8px;
`;

const AudioArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const AudioSubTitle = styled.span`
  @import 'stylesheets/design_system/font';
  @include body__medium;
  color: #676e7b;
  word-break: break-word;
`;

const AudioPlayer = styled.audio`
  width: 100%;
`;

const FeedbackForm = styled.div`
  margin-top: 24px;
`;

const Title = styled.h4`
  @import 'stylesheets/design_system/font';
  @include body__medium-medium;
  margin-bottom: 8px;
  color: var(--text-primary, #141413);
`;

const Subtitle = styled.p`
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 16px;
  color: $ds-gray-dark;
`;

const Input = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #cddaef;
  border-radius: 4px;
  font-size: 16px;
  border-color: $ds-gray-dark;
  @media (max-width: 768px) {
    min-height: 80px;
  }
`;

const InputArea = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  align-items: flex-start;
`;

const ArrowContainer = styled.span`
  transform: rotate(270deg);
`;

export default PodcastPlayer;
